/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-visit-sequence-grid/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import { DragAndDrop } from '@progress/kendo-react-common';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DragHandleCell from '@components/core/drag-and-drop/drag-handle-cell';
import DraggableRow from '@components/core/drag-and-drop/draggable-row';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ReorderContext } from '@components/core/grid-helper';
import { EditVisitSequenceGridInterface } from './edit-visit-sequence-grid.interface';
import { useTranslation } from 'hooks';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISITS_SEQUENCE } from 'queries/study-visit-schedules';
import { StudySubjectVisitSequenceInputType } from 'types/graphql-global-types';
import { convertBooleanToYesNo } from '@utils/convertor';
import NotificationService from '@utils/notification';
// eslint-disable-next-line max-len
import { StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit } from '@queries/generated/fragments.generated';
import {
    saveStudyVisitScheduleVisitsSequence,
    saveStudyVisitScheduleVisitsSequenceVariables,
} from '@queries/generated/study-visit-schedules.generated';

const CUSTOM_COLUMN_WIDTH = 83;

const EditVisitSequenceGrid = ({
    studyId,
    visitScheduleId,
    items,
    columns,
    onClose,
    onSaveSequence,
    orderChanged,
}: EditVisitSequenceGridInterface) => {
    const { t } = useTranslation();

    const minGridWidth = React.useRef<number>(0);
    const grid = React.useRef<HTMLDivElement | null>(null);

    const [gridData, setGridData] = useState<StudyVisitScheduleFields_StudySubjectVisit[]>(items);
    const activeItem = useRef<StudyVisitScheduleFields_StudySubjectVisit | null>(null);
    const [applyMinWidth, setApplyMinWidth] = React.useState(false);
    const [gridCurrent, setGridCurrent] = React.useState(0);
    const [updateStudyVisitSequence] = useMutation<
        saveStudyVisitScheduleVisitsSequence,
        saveStudyVisitScheduleVisitsSequenceVariables
    >(SAVE_STUDY_VISIT_SCHEDULE_VISITS_SEQUENCE);

    useEffect(() => {
        let visitsArray = [...items];
        visitsArray = orderBy(visitsArray, [{ field: 'sequenceNumber', dir: 'asc' }]) ?? [];
        const arrayWithFields = visitsArray.map((item: any) => {
            const readableLeadVisitId = visitsArray.find((visitFormGrid: any) => visitFormGrid.id === item.leadVisitId);
            return {
                ...item,
                active: convertBooleanToYesNo(item?.active),
                _readableLeadVisitId: readableLeadVisitId?.visitId ?? '',
            };
        });
        setGridData(arrayWithFields);
    }, [items]);

    const handleResize = useCallback(
        debounce(() => {
            if (grid.current.offsetWidth < minGridWidth.current) {
                setApplyMinWidth(true);
            } else if (grid.current.offsetWidth > minGridWidth.current) {
                setGridCurrent(grid.current.offsetWidth);
                setApplyMinWidth(false);
            }
        }, 200),
        [],
    );

    React.useEffect(() => {
        grid.current = document.querySelector('.reorder-grid');
        window.addEventListener('resize', handleResize);
        columns.forEach((item: IExpandableGridColumnV2) => {
            if (item.minWidth) {
                minGridWidth.current += item.minWidth;
            }
        });
        minGridWidth.current += CUSTOM_COLUMN_WIDTH;
        setGridCurrent(grid.current.offsetWidth);
        setApplyMinWidth(grid.current.offsetWidth < minGridWidth.current);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const setWidth = (minWidth = 0) =>
        applyMinWidth ? minWidth : minWidth + (gridCurrent - minGridWidth.current) / columns.length;

    const reorder = (dataItem: StudyVisitScheduleFields_StudySubjectVisit, direction: 'before' | 'after') => {
        if (activeItem.current === dataItem) {
            return;
        }
        const reorderedData = gridData.slice();
        const prevIndex = reorderedData.findIndex((p) => p === activeItem.current);
        const nextIndex = reorderedData.findIndex((p) => p === dataItem);
        const adjustIndex = direction === 'before' ? (prevIndex < nextIndex ? -1 : 0) : prevIndex < nextIndex ? 1 : 0;
        reorderedData.splice(prevIndex, 1);
        reorderedData.splice(Math.max(nextIndex + adjustIndex, 0), 0, activeItem.current || reorderedData[0]);
        orderChanged.setTrue();

        setGridData(reorderedData);
    };

    const dragStart = (dataItem: StudyVisitScheduleFields_StudySubjectVisit) => {
        activeItem.current = dataItem;
    };

    const updatedSequence = (itemsToUpdate: StudySubjectVisitSequenceInputType[]) => {
        updateStudyVisitSequence({
            variables: {
                visitScheduleId,
                studySubjectVisitSequence: itemsToUpdate,
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then(() => {
                onSaveSequence();
                NotificationService.showNotification(t('admin:editSequenceWarningUpdated'));
                orderChanged.setFalse();
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const onUpdateOrders = () => {
        const itemIndexesToUpdate: StudySubjectVisitSequenceInputType[] = [];
        gridData.forEach((item, index) => {
            if (item.sequenceNumber !== index + 1) {
                itemIndexesToUpdate.push({
                    sequenceNumber: index + 1,
                    visitId: item.id,
                });
            }
        });

        updatedSequence(itemIndexesToUpdate);
    };

    const getScrollAvailable = () => {
        const scrollableContainer = grid.current?.querySelector('.k-grid-content');
        if (scrollableContainer) {
            return scrollableContainer.scrollHeight - scrollableContainer.clientHeight;
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <>
            <div className="reorder-grid__heading">
                <h3 className="reorder-grid__title">{t('admin:visits')}</h3>
                <div className="reorder-grid__btns-group">
                    <button type="button" className="reorder-grid__btn" onClick={() => onUpdateOrders()}>
                        <FontAwesomeIcon
                            icon={['fal', 'circle-check']}
                            className="reorder-grid__icon reorder-grid__icon--save"
                        />
                    </button>
                    <button type="button" className="reorder-grid__btn" onClick={() => handleClose()}>
                        <FontAwesomeIcon
                            icon={['fal', 'circle-xmark']}
                            className="reorder-grid__icon reorder-grid__icon--close"
                        />
                    </button>
                </div>
            </div>
            <ReorderContext.Provider value={{ reorder, dragStart }}>
                <DragAndDrop>
                    <Grid
                        data={gridData}
                        className="reorder-grid"
                        dataItemKey="sequenceNumber"
                        rows={{
                            data: (rowProps) => <DraggableRow scrollAvailable={getScrollAvailable()} {...rowProps} />,
                        }}
                    >
                        <GridColumn
                            title={t('admin:sequence')}
                            width={CUSTOM_COLUMN_WIDTH}
                            cells={{ data: (props: GridCellProps) => DragHandleCell({ ...props, firstCol: true }) }}
                        />
                        {columns.map((column) => (
                            <GridColumn
                                {...column}
                                width={setWidth(column.minWidth)}
                                title={column.label}
                                cells={{ data: DragHandleCell }}
                            />
                        ))}
                    </Grid>
                </DragAndDrop>
            </ReorderContext.Provider>
        </>
    );
};

export default EditVisitSequenceGrid;
